import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetBasicStorefrontQuery, useGetStorefrontQuery } from '../../Api/Api';
import { SK_ORGANIZATION, SK_STOREFRONT } from '../../Constants/LocalStorageKeys';
import { LOCALIZATION_DEFAULTS, parseLocalizationConfig } from './localizationConfig';

// Resolves a localization key to its label: the merchant's per-storefront override
// (localizationConfig) if present, else the manifest default, else an inline fallback, else the
// key itself. Returns a stable `L(key, fallback?)` function.
//
//   const L = useLocalization();
//   <h2>{L('localization_cart_summary_title')}</h2>
//
// The inline fallback is optional (the manifest already carries the English default) but is handy
// at call sites for keys not yet added to the manifest.
export function useLocalization() {
    const settings = useGetStorefrontQuery();
    return useLocalizationFor(settings.data?.localizationConfig);
}

// For the pages rendered outside <Layout> (login, signup, token, 404). Those are unauthenticated,
// and getStorefront is token-gated — prepareHeaders throws without a session token — so they cannot
// use useLocalization(). getBasicStorefront is exempt from that gate and returns the same settings,
// but it needs the storefront identified explicitly: from the route params where the route carries
// them, else from the last storefront this browser saw (the token page persists these on login).
// With neither, no storefront is identifiable and every key resolves to its English default.
export function useLocalizationUnauthenticated() {
    const { organization, storefront } = useParams<{ organization?: string, storefront?: string }>();
    const org = organization ?? localStorage.getItem(SK_ORGANIZATION) ?? '';
    const sf = storefront ?? localStorage.getItem(SK_STOREFRONT) ?? '';
    const settings = useGetBasicStorefrontQuery({ organization: org, storefront: sf }, { skip: !org || !sf });
    return useLocalizationFor(settings.data?.localizationConfig);
}

// Same resolution, but reading the config from a caller-supplied source instead of the storefront
// settings query — for unauthenticated pages that already hold a getBasicStorefront result.
export function useLocalizationFor(localizationConfig?: string) {
    const overrides = React.useMemo(
        () => parseLocalizationConfig(localizationConfig),
        [localizationConfig]
    );
    return React.useCallback(
        (key: string, fallback?: string): string => {
            const override = overrides[key];
            if (override !== undefined && override !== '') return override;
            return LOCALIZATION_DEFAULTS[key] ?? fallback ?? key;
        },
        [overrides]
    );
}
